import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  SwipeableDrawer,
  Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import { usePrimaryFetchedData } from "../../context/PrimaryFetchedDataContext";
import { useUserControls } from "../../context/UserControlsProvider";
import { ItemStatus } from "../../types/DB2";
import { formatStatusLabel } from "../../utils/status";

const GlobalSnackbar: React.FC = () => {
  const {
    showSnackbar,
    snackbarOpen,
    snackbarOptions,
    hideSnackbar,
    lastScannedItem,
    selectedShipmentIndex,
    lookupItemInSelectedShipment,
  } = useUserControls();
  const { updateItemStatus } = usePrimaryFetchedData();
  const [snackbarDrawerOpen, setSnackbarDrawerOpen] = useState<boolean>(false);

  useEffect(() => {
    document.addEventListener("keydown", (event) => {
      if (event.key === "Space") {
        showSnackbar({
          message: "Space key pressed",
          duration: 3000,
          severity: "info",
        });
      }
    });
  }, []);

  const handleUpdateStatus = async (status: ItemStatus) => {
    if (!lastScannedItem) return;
    setSnackbarDrawerOpen(false);
    const normalized = lastScannedItem.trim().replace(/\s+/g, "");
    const lastScannedItemSnapshot =
      lookupItemInSelectedShipment(normalized) ||
      lookupItemInSelectedShipment(normalized, { allowSuffixMatch: true });
    if (!lastScannedItemSnapshot) {
      return;
    }
    await updateItemStatus({
      itemId: lastScannedItemSnapshot.id,
      orderId: lastScannedItemSnapshot.orderId!,
      newStatus: status,
      shipmentIndex: selectedShipmentIndex,
      newProcessedState: true,
      updateMethod: "manual",
      prevStatus: lastScannedItemSnapshot.status as ItemStatus,
    });
    showSnackbar({
      message: `Item status updated to ${formatStatusLabel(status) || status}`,
      duration: 3000,
      severity: "success",
    });
  };

  const handleEditClick = () => {
    setSnackbarDrawerOpen(true);
    hideSnackbar();
  };

  const action = (
    <React.Fragment>
      <Button color="primary" size="small" onClick={handleEditClick}>
        Edit
      </Button>
    </React.Fragment>
  );

  const severity = snackbarOptions.severity ?? "info";
  const isSuccess = severity === "success";
  const isWarning = severity === "warning";

  const alertStyles = {
    width: "100%",
    ...(isSuccess
      ? {
          backgroundColor: green[50],
          color: green[900],
          border: `1px solid ${green[200]}`,
        }
      : {}),
    ...(isSuccess
      ? {
          "& .MuiAlert-icon": {
            color: green[800],
          },
        }
      : isWarning
      ? {
          "& .MuiAlert-icon": {
            color: red[800],
          },
        }
      : {}),
  };

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarOptions.duration ?? 4000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        style={{ marginTop: "calc(var(--safe-area-extra-top, 0px) + 120px)" }}
      >
        <Alert
          action={action}
          onClose={hideSnackbar}
          severity={severity}
          sx={alertStyles}
        >
          {snackbarOptions.message}
        </Alert>
      </Snackbar>
      <SwipeableDrawer
        anchor="bottom"
        open={snackbarDrawerOpen}
        onClose={() => setSnackbarDrawerOpen(false)}
        onOpen={() => setSnackbarDrawerOpen(true)}
        disableSwipeToOpen
      >
        <div style={{ padding: 8, maxHeight: "50vh", overflow: "auto" }}>
          <Typography variant="subtitle1" sx={{ px: 1, py: 0.5 }}>
            Update Status on {lastScannedItem}{" "}
          </Typography>
          <List>
            <ListItem
              onClick={() => {
                handleUpdateStatus("damaged");
              }}
            >
              <ListItemText primary="Damaged" />
            </ListItem>
            <ListItem
              onClick={() => {
                handleUpdateStatus("missing");
              }}
            >
              <ListItemText primary="Missing" />
            </ListItem>
          </List>
        </div>
      </SwipeableDrawer>
    </>
  );
};

export default GlobalSnackbar;
